import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

interface PremiumLoaderProps {
  /** Size variant of the loader */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Whether to show the loader in fullscreen mode */
  fullScreen?: boolean;
  /** Custom text to display below the loader */
  text?: string;
  /** Additional className for the container */
  className?: string;
}

const sizeConfig = {
  xs: {
    container: 'w-4 h-4',
    ring: 'w-4 h-4 border-[1.5px]',
    dot: 'w-1 h-1',
    text: 'text-xs',
  },
  sm: {
    container: 'w-6 h-6',
    ring: 'w-6 h-6 border-2',
    dot: 'w-1.5 h-1.5',
    text: 'text-xs',
  },
  md: {
    container: 'w-8 h-8',
    ring: 'w-8 h-8 border-2',
    dot: 'w-2 h-2',
    text: 'text-sm',
  },
  lg: {
    container: 'w-12 h-12',
    ring: 'w-12 h-12 border-[3px]',
    dot: 'w-2.5 h-2.5',
    text: 'text-sm',
  },
  xl: {
    container: 'w-16 h-16',
    ring: 'w-16 h-16 border-4',
    dot: 'w-3 h-3',
    text: 'text-base',
  },
};

/**
 * Premium animated loader component with consistent styling across the app.
 * Features a rotating ring with a pulsing dot in the center.
 */
export default function PremiumLoader({ 
  size = 'md', 
  fullScreen = false, 
  text,
  className 
}: PremiumLoaderProps) {
  const config = sizeConfig[size];

  const loaderContent = (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      {/* Loader animation container */}
      <div className={cn('relative', config.container)}>
        {/* Outer rotating ring */}
        <motion.div
          className={cn(
            config.ring,
            'rounded-full border-slate/20 border-t-coral absolute inset-0'
          )}
          animate={{ rotate: 360 }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
        
        {/* Inner pulsing ring */}
        <motion.div
          className={cn(
            config.ring,
            'rounded-full border-transparent border-l-slate-light/30 absolute inset-0'
          )}
          animate={{ rotate: -360 }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
        
        {/* Center dot with pulse effect */}
        <motion.div
          className={cn(
            config.dot,
            'rounded-full bg-coral absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
          )}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      {/* Optional text */}
      {text && (
        <motion.p
          className={cn(config.text, 'text-text-muted font-medium')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {text}
        </motion.p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-void/95 backdrop-blur-sm flex items-center justify-center z-50">
        {loaderContent}
      </div>
    );
  }

  return loaderContent;
}

/**
 * Inline loader for use inside buttons - uses minimal size with no text
 */
export function InlineLoader({ className }: { className?: string }) {
  return (
    <motion.div
      className={cn('w-4 h-4 relative', className)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="w-4 h-4 rounded-full border-[1.5px] border-white/20 border-t-white absolute inset-0"
        animate={{ rotate: 360 }}
        transition={{
          duration: 0.8,
          repeat: Infinity,
          ease: 'linear',
        }}
      />
    </motion.div>
  );
}

/**
 * Page loader wrapper - centers the loader in a full-height container
 */
export function PageLoader({ text }: { text?: string }) {
  return (
    <div className="min-h-screen bg-void flex items-center justify-center">
      <PremiumLoader size="lg" text={text} />
    </div>
  );
}

/**
 * Section loader - for use within dashboard sections/cards
 */
export function SectionLoader({ text, className }: { text?: string; className?: string }) {
  return (
    <div className={cn('h-full flex items-center justify-center py-12', className)}>
      <PremiumLoader size="md" text={text} />
    </div>
  );
}

